import React, { useState, useEffect } from 'react';
import { Cloud, CloudRain, Waves, Wind, Trees, Volume2, VolumeX, CloudFog, AlertCircle, Play, Square, CircleChevronDown, CircleChevronUp, Info } from 'lucide-react';
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getSoundUrl, SoundCategories, ambientSoundPresets } from '@/config/audioConfig';
import CategoryGroup from './CategoryGroup';
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export interface AmbienceSound {
  id: string;
  name: string;
  icon: React.ElementType;
  volume: number;
  path?: string; // Optional path property for the sound file
  category?: SoundCategories; // Sound category for organization
}

interface AmbienceMixerProps {
  sounds: AmbienceSound[];
  onVolumeChange: (id: string, volume: number) => void;
  isPlaying?: boolean; // Whether a binaural session is currently playing
}

const AmbienceMixer: React.FC<AmbienceMixerProps> = ({ sounds, onVolumeChange, isPlaying = false }) => {
  const [mutedSounds, setMutedSounds] = useState<Set<string>>(new Set());
  const [lastVolumes, setLastVolumes] = useState<Record<string, number>>({});
  const [soundsLoaded, setSoundsLoaded] = useState<Record<string, boolean>>({});
  const [previewingSound, setPreviewingSound] = useState<string | null>(null);
  const [previewAudio, setPreviewAudio] = useState<HTMLAudioElement | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  
  const anyExpanded = Object.values(expandedCategories).some(value => value === true);
  
  useEffect(() => {
    const initialVolumes: Record<string, number> = {};
    const initialExpandedState: Record<string, boolean> = {};
    
    initialExpandedState['default'] = true;
    initialExpandedState[SoundCategories.NATURE] = false;
    initialExpandedState[SoundCategories.AMBIENT] = false;
    initialExpandedState[SoundCategories.INSTRUMENTS] = false;
    initialExpandedState[SoundCategories.MEDITATION] = false;
    initialExpandedState[SoundCategories.URBAN] = false;
    
    sounds.forEach(sound => {
      initialVolumes[sound.id] = sound.volume;
      
      if (!sound.path || sound.path === '') {
        const configSound = ambientSoundPresets.find(s => s.id === sound.id);
        if (configSound && configSound.path) {
          sound.path = configSound.path;
          console.log(`Set default path for ${sound.id} sound: ${sound.path}`);
        }
      }
    });
    
    setLastVolumes(initialVolumes);
    setExpandedCategories(initialExpandedState);
    
    setTimeout(() => {
      toast.info("Ambient Sound Tip", {
        description: "Use headphones for the best ambient sound experience",
        duration: 5000,
      });
    }, 3000);

    return () => {
      if (previewAudio) {
        previewAudio.pause();
        previewAudio.src = '';
      }
    };
  }, []);
  
  const toggleMute = (id: string) => {
    setMutedSounds(prev => {
      const newMuted = new Set(prev);
      const soundName = sounds.find(s => s.id === id)?.name || id;
      
      if (newMuted.has(id)) {
        newMuted.delete(id);
        const previousVolume = lastVolumes[id] || 30;
        onVolumeChange(id, previousVolume);
        toast.success(`${soundName} unmuted`);
      } else {
        newMuted.add(id);
        setLastVolumes(prev => ({
          ...prev,
          [id]: sounds.find(s => s.id === id)?.volume || 0
        }));
        onVolumeChange(id, 0);
        toast.success(`${soundName} muted`);
      }
      return newMuted;
    });
  };

  const handleVolumeChange = (id: string, value: number) => {
    if (typeof value !== 'number' || isNaN(value)) {
      console.error(`Invalid volume for ${id}: ${value}`);
      value = 0;
    }
    
    // For specific percentage values, ensure exact match
    if (![0, 25, 50, 75, 100].includes(value)) {
      // For other values, round to nearest whole number and clamp between 0 and 100
      value = Math.round(Math.max(0, Math.min(100, value)));
    }
    
    console.log(`Setting volume for ${id} to ${value}%`);
    
    if (value > 0) {
      setLastVolumes(prev => ({
        ...prev,
        [id]: value
      }));
      
      if (mutedSounds.has(id)) {
        setMutedSounds(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }
    }
    
    onVolumeChange(id, value);
  };

  const getSoundPath = (sound: AmbienceSound): string => {
    if (sound.path && sound.path !== '') {
      return sound.path;
    }
    
    // Find the sound in the config presets
    const configSound = ambientSoundPresets.find(s => s.id === sound.id);
    if (configSound && configSound.path) {
      return configSound.path;
    }
    
    console.error(`No path found for sound ${sound.id}`);
    return "Unknown";
  };
  
  const previewSound = (id: string) => {
    if (previewAudio) {
      previewAudio.pause();
      previewAudio.src = '';
      setPreviewAudio(null);
    }
    
    if (previewingSound === id) {
      setPreviewingSound(null);
      return;
    }

    const sound = sounds.find(s => s.id === id);
    if (!sound) return;
    
    try {
      const audio = new Audio();
      
      let soundPath = sound.path;
      
      if (!soundPath) {
        const configSound = ambientSoundPresets.find(s => s.id === sound.id);
        if (configSound && configSound.path) {
          soundPath = configSound.path;
          console.log(`Using config path for ${sound.id} sound: ${soundPath}`);
        }
      }
      
      if (!soundPath) {
        console.error(`No path found for sound ${id}`);
        toast.error(`Couldn't play ${sound.name}`, {
          description: "Sound file not found"
        });
        return;
      }
      
      audio.src = getSoundUrl(soundPath);
      
      let volume = sound.volume;
      if (typeof volume !== 'number' || isNaN(volume) || volume < 0 || volume > 100) {
        console.warn(`Invalid volume for ${id}: ${volume}, defaulting to 50%`);
        volume = 50;
      }
      
      audio.volume = volume / 100;
      console.log(`Previewing sound: ${id} (${sound.name}) with volume ${audio.volume} from ${audio.src}`);
      
      audio.play().then(() => {
        toast.success(`Playing ${sound.name} preview`);
        setPreviewingSound(id);
        setPreviewAudio(audio);
        
        setTimeout(() => {
          if (audio) {
            audio.pause();
            audio.src = '';
            setPreviewingSound(null);
            setPreviewAudio(null);
          }
        }, 5000);
      }).catch(err => {
        console.error("Failed to play preview:", err);
        toast.error(`Couldn't play ${sound.name}`, {
          description: "Try clicking the volume buttons first to unlock audio"
        });
      });
      
      audio.onended = () => {
        setPreviewingSound(null);
        setPreviewAudio(null);
      };
    } catch (error) {
      console.error("Error previewing sound:", error);
      toast.error("Failed to preview sound");
    }
  };
  
  const expandAllCategories = () => {
    const categories = Object.keys(categorizedSounds);
    const newExpandedState: Record<string, boolean> = {};
    
    categories.forEach(category => {
      newExpandedState[category] = true;
    });
    
    setExpandedCategories(newExpandedState);
    toast.success("Expanded all sound categories");
  };
  
  const collapseAllCategories = () => {
    const categories = Object.keys(categorizedSounds);
    const newExpandedState: Record<string, boolean> = {};
    
    categories.forEach(category => {
      newExpandedState[category] = false;
    });
    
    newExpandedState['default'] = true;
    
    setExpandedCategories(newExpandedState);
    toast.success("Collapsed all sound categories");
  };

  const defaultSound = sounds.find(sound => sound.id === 'rain');
  
  const categorizedSounds: Record<string, AmbienceSound[]> = sounds.reduce((acc, sound) => {
    if (sound.id === 'rain') return acc;
    
    const category = sound.category || SoundCategories.AMBIENT;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(sound);
    return acc;
  }, {} as Record<string, AmbienceSound[]>);
  
  if (defaultSound) {
    categorizedSounds['default'] = [defaultSound];
  }

  const orderedCategories = [
    'default',
    SoundCategories.SOUNDSCAPES,
    SoundCategories.NATURE,
    SoundCategories.AMBIENT,
    SoundCategories.MEDITATION,
    SoundCategories.INSTRUMENTS,
    SoundCategories.URBAN
  ].filter(cat => {
    if (cat === 'default') return true;
    return categorizedSounds[cat]?.length > 0;
  });

  const getCategoryDisplayName = (category: string): string => {
    if (category === 'default') return "Default";
    
    switch (category) {
      case SoundCategories.NATURE:
        return "Nature";
      case SoundCategories.AMBIENT:
        return "Ambient";
      case SoundCategories.INSTRUMENTS:
        return "Instruments";
      case SoundCategories.MEDITATION:
        return "Meditation";
      case SoundCategories.URBAN:
        return "Urban";
      default:
        return category.charAt(0).toUpperCase() + category.slice(1);
    }
  };

  const SoundControl = ({ sound }: { sound: AmbienceSound }) => {
    const soundPath = getSoundPath(sound);
    
    return (
      <div key={sound.id} className="flex items-center gap-4">
        <div className="h-8 w-8 rounded-full bg-secondary/50 flex items-center justify-center">
          <sound.icon size={18} className="text-foreground opacity-70" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium">{sound.name}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="h-4 w-4 flex items-center justify-center text-muted-foreground hover:text-foreground">
                    <Info size={12} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" align="start" className="max-w-[300px]">
                  <div className="text-xs space-y-1">
                    <p><strong>Sound:</strong> {sound.name}</p>
                    <p><strong>Source:</strong> {soundPath}</p>
                    <p><strong>ID:</strong> {sound.id}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{sound.volume}%</span>
              
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6" 
                onClick={() => toggleMute(sound.id)}
              >
                {mutedSounds.has(sound.id) ? (
                  <VolumeX size={16} />
                ) : (
                  <Volume2 size={16} />
                )}
              </Button>
            </div>
          </div>
          
          <Slider
            value={[sound.volume]}
            min={0}
            max={100}
            step={1}
            onValueChange={(values) => handleVolumeChange(sound.id, values[0])}
            className={mutedSounds.has(sound.id) ? "opacity-50" : ""}
          />
          
          <div className="flex justify-between mt-1">
            <button 
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => handleVolumeChange(sound.id, 0)}
            >
              0%
            </button>
            <button 
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => handleVolumeChange(sound.id, 25)}
            >
              25%
            </button>
            <button 
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => handleVolumeChange(sound.id, 50)}
            >
              50%
            </button>
            <button 
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => handleVolumeChange(sound.id, 75)}
            >
              75%
            </button>
            <button 
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => handleVolumeChange(sound.id, 100)}
            >
              100%
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="ambient-mixer glass rounded-2xl p-4 animate-fade-in transition-all duration-300">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-medium">Ambient Sounds</h3>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="icon" 
            className="flex items-center justify-center w-8 h-8" 
            onClick={expandAllCategories}
            title="Expand All"
          >
            <CircleChevronDown size={20} />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="flex items-center justify-center w-8 h-8" 
            onClick={collapseAllCategories}
            title="Collapse All"
          >
            <CircleChevronUp size={20} />
          </Button>
        </div>
      </div>
      
      <div className="space-y-2">
        {orderedCategories.map(category => (
          <CategoryGroup 
            key={category} 
            title={getCategoryDisplayName(category)}
            defaultOpen={expandedCategories[category] || false}
            className=""
            onOpenChange={(isOpen) => {
              setExpandedCategories(prev => ({
                ...prev,
                [category]: isOpen
              }));
            }}
          >
            <div className="space-y-3">
              {categorizedSounds[category]?.map(sound => (
                <SoundControl key={sound.id} sound={sound} />
              ))}
            </div>
          </CategoryGroup>
        ))}
      </div>
      
      <div className="mt-3 text-xs text-muted-foreground flex items-center gap-2">
        <AlertCircle size={14} />
        <span>Sounds may take a moment to load.</span>
      </div>
    </div>
  );
};

export default AmbienceMixer;